export const HERO_OVERLAY_DEFAULT = "linear-gradient(to bottom, rgba(0,0,0,.14), rgba(8,8,20,.72))";

export const HERO_OVERLAY_DIRECTIONS = [
  { value: "to bottom", label: "Arriba → abajo" },
  { value: "to top", label: "Abajo → arriba" },
  { value: "to right", label: "Izquierda → derecha" },
  { value: "to left", label: "Derecha → izquierda" },
  { value: "to bottom right", label: "Diagonal ↘" },
  { value: "to bottom left", label: "Diagonal ↙" },
  { value: "to top right", label: "Diagonal ↗" },
  { value: "to top left", label: "Diagonal ↖" },
];

export const HERO_OVERLAY_PRESETS = [
  {
    id: "soft",
    label: "Suave",
    config: {
      direction: "to bottom",
      startColor: "#000000",
      startOpacity: 8,
      endColor: "#080814",
      endOpacity: 48,
    },
  },
  {
    id: "readable",
    label: "Legible",
    config: {
      direction: "to bottom",
      startColor: "#000000",
      startOpacity: 14,
      endColor: "#080814",
      endOpacity: 72,
    },
  },
  {
    id: "strong",
    label: "Intensa",
    config: {
      direction: "to bottom",
      startColor: "#000000",
      startOpacity: 28,
      endColor: "#080814",
      endOpacity: 82,
    },
  },
];

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function byteToHex(value) {
  return clamp(Math.round(value), 0, 255).toString(16).padStart(2, "0");
}

function normalizeHex(hex) {
  const raw = String(hex || "").trim().toLowerCase();
  if (/^#[0-9a-f]{6}$/.test(raw)) return raw;
  if (/^#[0-9a-f]{3}$/.test(raw)) {
    return `#${raw[1]}${raw[1]}${raw[2]}${raw[2]}${raw[3]}${raw[3]}`;
  }
  return "";
}

function alphaToPercent(value) {
  const raw = String(value || "").trim();
  if (!raw) return null;
  if (raw.endsWith("%")) {
    const percent = Number(raw.slice(0, -1));
    return Number.isFinite(percent) ? clamp(Math.round(percent), 0, 100) : null;
  }
  const alpha = Number(raw);
  return Number.isFinite(alpha) ? clamp(Math.round(alpha * 100), 0, 100) : null;
}

function parseColorToken(token) {
  const value = String(token || "").trim();
  if (!value) return null;

  if (/^transparent$/i.test(value)) {
    return { color: "#000000", opacity: 0 };
  }

  let match = value.match(/^#([0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i);
  if (match) {
    const hex = match[1].toLowerCase();
    if (hex.length === 3 || hex.length === 6) {
      const normalized = normalizeHex(`#${hex}`);
      return normalized ? { color: normalized, opacity: 100 } : null;
    }
    if (hex.length === 4) {
      const color = normalizeHex(`#${hex[0]}${hex[1]}${hex[2]}`);
      const alpha = parseInt(`${hex[3]}${hex[3]}`, 16);
      return { color, opacity: Math.round((alpha / 255) * 100) };
    }
    const color = `#${hex.slice(0, 6)}`;
    const alpha = parseInt(hex.slice(6), 16);
    return { color, opacity: Math.round((alpha / 255) * 100) };
  }

  match = value.match(/^rgba?\(\s*([+-]?(?:\d+\.?\d*|\.\d+))\s*,\s*([+-]?(?:\d+\.?\d*|\.\d+))\s*,\s*([+-]?(?:\d+\.?\d*|\.\d+))(?:\s*,\s*([+-]?(?:\d+\.?\d*|\.\d+)%?))?\s*\)$/i);
  if (match) {
    const red = Number(match[1]);
    const green = Number(match[2]);
    const blue = Number(match[3]);
    if (![red, green, blue].every(Number.isFinite)) return null;
    if ([red, green, blue].some((channel) => channel < 0 || channel > 255)) return null;
    const opacity = match[4] === undefined ? 100 : alphaToPercent(match[4]);
    if (opacity === null) return null;
    return {
      color: `#${byteToHex(red)}${byteToHex(green)}${byteToHex(blue)}`,
      opacity,
    };
  }

  return null;
}

function splitTopLevel(value) {
  const items = [];
  let depth = 0;
  let start = 0;
  for (let index = 0; index < value.length; index += 1) {
    const character = value[index];
    if (character === "(") depth += 1;
    if (character === ")") depth -= 1;
    if (depth < 0) return [];
    if (character === "," && depth === 0) {
      items.push(value.slice(start, index).trim());
      start = index + 1;
    }
  }
  if (depth !== 0) return [];
  items.push(value.slice(start).trim());
  return items.filter(Boolean);
}

function normalizeDirection(value) {
  const candidate = String(value || "").trim().toLowerCase().replace(/\s+/g, " ");
  return HERO_OVERLAY_DIRECTIONS.some((option) => option.value === candidate)
    ? candidate
    : "";
}

export function parseHeroOverlay(value) {
  const raw = String(value ?? "").trim();
  if (!raw) {
    return { kind: "empty", raw: "" };
  }

  const match = raw.match(/^linear-gradient\((.*)\)$/i);
  if (!match) {
    return { kind: "legacy", raw };
  }

  const parts = splitTopLevel(match[1]);
  if (parts.length < 2 || parts.length > 3) {
    return { kind: "legacy", raw };
  }

  let direction = "to bottom";
  let colorTokens = parts;
  if (parts.length === 3) {
    direction = normalizeDirection(parts[0]);
    if (!direction) return { kind: "legacy", raw };
    colorTokens = parts.slice(1);
  }

  if (colorTokens.length !== 2) return { kind: "legacy", raw };
  const start = parseColorToken(colorTokens[0]);
  const end = parseColorToken(colorTokens[1]);
  if (!start || !end) return { kind: "legacy", raw };

  return {
    kind: "editable",
    raw,
    config: {
      direction,
      startColor: start.color,
      startOpacity: start.opacity,
      endColor: end.color,
      endOpacity: end.opacity,
    },
  };
}

function channelValues(hex) {
  const normalized = normalizeHex(hex) || "#000000";
  return [
    parseInt(normalized.slice(1, 3), 16),
    parseInt(normalized.slice(3, 5), 16),
    parseInt(normalized.slice(5, 7), 16),
  ];
}

function formatAlpha(percent) {
  const value = clamp(Math.round(Number(percent) || 0), 0, 100) / 100;
  if (value === 0 || value === 1) return String(value);
  return value.toFixed(2).replace(/0+$/, "").replace(/\.$/, "");
}

function rgba(hex, opacity) {
  const [red, green, blue] = channelValues(hex);
  return `rgba(${red}, ${green}, ${blue}, ${formatAlpha(opacity)})`;
}

export function serializeHeroOverlay(config) {
  const direction = normalizeDirection(config?.direction) || "to bottom";
  return `linear-gradient(${direction}, ${rgba(config?.startColor, config?.startOpacity)}, ${rgba(config?.endColor, config?.endOpacity)})`;
}

export function heroOverlayPresetId(config) {
  if (!config) return null;
  const serialized = serializeHeroOverlay(config);
  const preset = HERO_OVERLAY_PRESETS.find(
    (candidate) => serializeHeroOverlay(candidate.config) === serialized
  );
  return preset?.id || null;
}

export function defaultHeroOverlayConfig() {
  const parsed = parseHeroOverlay(HERO_OVERLAY_DEFAULT);
  return parsed.kind === "editable"
    ? { ...parsed.config }
    : { ...HERO_OVERLAY_PRESETS[1].config };
}
